import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { EXPORT_BATCH_SIZE } from '../../utils/exportHelpers';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Paper, Typography, Button, ListItemText, Menu, MenuItem, Portal } from '@mui/material';
import { CloudUpload, Plus } from 'lucide-react';
import CommonTable from '../../common_components/CommonTable';
import Pagination from '../../common_components/Pagination';
import TableToolbar from '../../common_components/TableToolbar';
import CommonLoader from '../../common_components/CommonLoader';
import PageContainer from '../../common_components/PageContainer';
import PagesCommonHeader from '../../common_components/PagesCommonHeader';
import LastUpdatedInfo from '../../common_components/LastUpdatedInfo';
import { Dropdown, ExportButton } from '../../common_components/FormInputs';
import { fetchDropdownOptions, fetchPreferencesThunk, savePreferencesThunk, selectPreferences, } from '../../redux/opportunitySlice';
import { toast } from '../../common_components/Toast/ToastUtils';
import ConfirmActionModal from '../../common_components/ConfirmActionModal';
import { setActiveModuleKey } from '../../redux/userDataSlice';
import NotesModal from '../../common_components/NotesModal';
import MediaKitDrawer from './components/MediaKitDrawer';
const DropdownDisplay = ({ icon, label, value }) => (_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [_jsx("img", { src: icon, alt: label }), _jsxs(Typography, { className: 'all-Font-Sizes', sx: { fontSize: '13px !important', fontWeight: 500, color: '#344054' }, children: [label, ":"] }), _jsx(Typography, { className: 'all-Font-Sizes', sx: { fontSize: '13px !important', color: '#667085', textTransform: 'capitalize' }, children: value })] }));
const EmptyState = ({ loading }) => (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }, children: loading ? _jsx(CommonLoader, { className: "table-loader" }) : (_jsx(Typography, { sx: { color: '#667085', fontSize: 14 }, children: "No records found." })) }));
// ✅ Preferences helper functions
const getSettingsFromPreferences = (preferences, moduleKey) => {
    return preferences?.[moduleKey] || [];
};
const getHiddenColumnsFromPreferences = (preferences, moduleKey) => {
    const hiddenSet = new Set();
    const prefs = getSettingsFromPreferences(preferences, moduleKey);
    if (!prefs.length)
        return hiddenSet;
    prefs.forEach((item) => {
        const key = Object.keys(item)[0];
        if (item[key]?.visibility === false) {
            hiddenSet.add(key);
        }
    });
    return hiddenSet;
};
const getColumnOrderFromPreferences = (preferences, moduleKey, defaultColumns) => {
    const prefs = getSettingsFromPreferences(preferences, moduleKey);
    if (!prefs.length)
        return defaultColumns.map(c => c.id);
    const ordered = [];
    const prefKeys = [];
    prefs.forEach((item) => {
        const key = Object.keys(item)[0];
        prefKeys.push(key);
        ordered.push({ key, order: item[key]?.order ?? 999 });
    });
    ordered.sort((a, b) => a.order - b.order);
    const sortedKeys = ordered.map(o => o.key);
    const remaining = defaultColumns
        .map(c => c.id)
        .filter(id => !prefKeys.includes(id));
    return [...sortedKeys, ...remaining];
};
/** Action buttons with menu and portal-based backdrop */
const ACTION_BY_MODULE = {
    content_opportunities: 'Manage Content',
    creator_directory: 'Manage Creator',
    partner_directory: 'Manage Partner',
};
export const ActionButtons = ({ row, moduleKey, onEdit, onDelete, onSuccess }) => {
    const permissions = useSelector((state) => state.auth.permissions);
    const requiredAction = ACTION_BY_MODULE[moduleKey];
    const hasManageAccess = !requiredAction || (permissions?.[moduleKey]?.actions || []).includes(requiredAction);
    const [anchorEl, setAnchorEl] = useState(null);
    const [notesModalOpen, setNotesModalOpen] = useState(false);
    const [mediaKitDrawerOpen, setMediaKitDrawerOpen] = useState(false);
    const open = Boolean(anchorEl);
    const handleClose = () => setAnchorEl(null);
    const handleClick = (e) => setAnchorEl(e.currentTarget);
    const hasNotes = Boolean(row.notes && row.notes.trim().length > 0);
    const hasMediaKit = Boolean(row.upload_files && row.upload_files.length > 0);
    const btnStyle = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '6px',
        borderRadius: '6px',
        border: 'none',
        background: 'transparent',
        cursor: 'pointer',
        transition: 'background 0.2s',
        position: 'relative',
    };
    const redDotStyle = {
        position: 'absolute',
        top: '4px',
        right: '4px',
        width: '6px',
        height: '6px',
        backgroundColor: '#F04438',
        borderRadius: '50%',
        border: '1px solid #fff',
    };
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 0.5 }, children: [_jsxs("button", { style: btnStyle, onClick: () => setNotesModalOpen(true), onMouseEnter: (e) => (e.currentTarget.style.background = '#F2F4F7'), onMouseLeave: (e) => (e.currentTarget.style.background = 'transparent'), title: "Notes", children: [_jsx("img", { src: "/assets/book.svg", alt: "Note", style: { width: 16, height: 16 } }), hasNotes && _jsx("div", { style: redDotStyle })] }), _jsxs("button", { style: {
                            ...btnStyle,
                            cursor: hasMediaKit ? 'pointer' : 'default',
                            opacity: hasMediaKit ? 1 : 0.4,
                            filter: hasMediaKit ? 'none' : 'grayscale(100%)',
                        }, onClick: () => hasMediaKit && setMediaKitDrawerOpen(true), onMouseEnter: (e) => hasMediaKit && (e.currentTarget.style.background = '#F2F4F7'), onMouseLeave: (e) => hasMediaKit && (e.currentTarget.style.background = 'transparent'), title: hasMediaKit ? "View Files" : "No Files", children: [_jsx("img", { src: "/assets/mediakit.svg", alt: "Uploads", style: { width: 16, height: 16 } }), hasMediaKit && _jsx("div", { style: redDotStyle })] }), hasManageAccess && (_jsx("button", { style: btnStyle, onClick: handleClick, onMouseEnter: (e) => (e.currentTarget.style.background = '#F2F4F7'), onMouseLeave: (e) => (e.currentTarget.style.background = 'transparent'), children: _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", children: [_jsx("circle", { cx: "12", cy: "5", r: "2", fill: "#667085" }), _jsx("circle", { cx: "12", cy: "12", r: "2", fill: "#667085" }), _jsx("circle", { cx: "12", cy: "19", r: "2", fill: "#667085" })] }) }))] }), _jsx(NotesModal, { open: notesModalOpen, onClose: () => setNotesModalOpen(false), id: row._id, initialNotes: row.notes || "", onSuccess: onSuccess }), _jsx(MediaKitDrawer, { open: mediaKitDrawerOpen, onClose: () => setMediaKitDrawerOpen(false), files: row.upload_files || [] }), open && (_jsx(Portal, { children: _jsx(Box, { onClick: handleClose, sx: {
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(0,0,0,0.15)',
                        zIndex: 1400,
                        cursor: 'default',
                    } }) })), _jsxs(Menu, { anchorEl: anchorEl, open: open, onClose: handleClose, disableScrollLock: true, anchorOrigin: { vertical: 'bottom', horizontal: 'right' }, transformOrigin: { vertical: 'top', horizontal: 'right' }, sx: { zIndex: 1500 }, PaperProps: {
                    sx: {
                        borderRadius: '8px',
                        minWidth: 160,
                        boxShadow: '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
                        border: '1px solid #EAECF0',
                        p: 0.5,
                    },
                }, children: [_jsxs(MenuItem, { onClick: () => {
                            handleClose();
                            onEdit(row);
                        }, sx: { borderRadius: '6px', py: 1, mb: 0.5, mx: 0.5, '&:hover': { bgcolor: '#F9FAFB' } }, children: [_jsx("img", { src: "/assets/edit.svg", alt: "Edit" }), _jsx(ListItemText, { primary: "Edit", primaryTypographyProps: { fontSize: '14px', fontWeight: 500, px: 1 } })] }), _jsxs(MenuItem, { onClick: () => {
                            handleClose();
                            onDelete(row._id);
                        }, sx: { borderRadius: '6px', py: 1, mx: 0.5, '&:hover': { bgcolor: '#F9FAFB' } }, children: [_jsx("img", { src: "/assets/delete.svg", alt: "Delete" }), _jsx(ListItemText, { primary: "Delete", primaryTypographyProps: { fontSize: '14px', fontWeight: 500, px: 1 } })] })] })] }));
};
const DirectoryBase = ({ moduleKey, pageTitle, addNewButtonLabel, uploadButtonLabel, fetchThunk, deleteThunk, exportFn, renderCell, columns, filtersConfig, AddNewDrawer, UploadDrawer, selectors, itemsPerPage = 20, renderHeaderExtra, extraParams = {}, }) => {
    const dispatch = useDispatch();
    const items = useSelector(selectors.items);
    const loading = useSelector(selectors.loading);
    const total = useSelector(selectors.total);
    const lastUpdated = useSelector(selectors.lastUpdated);
    const preferences = useSelector(selectPreferences);
    const permissions = useSelector((state) => state.auth.permissions);
    const requiredAction = ACTION_BY_MODULE[moduleKey];
    const hasManageAccess = !requiredAction || (permissions?.[moduleKey]?.actions || []).includes(requiredAction);
    const [filters, setFilters] = useState(() => {
        const initial = {};
        filtersConfig.forEach(f => initial[f.key] = 'all');
        return initial;
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedRows, setSelectedRows] = useState(new Set());
    const [exporting, setExporting] = useState(false);
    const [uploadDrawerOpen, setUploadDrawerOpen] = useState(false);
    const [addNewDrawerOpen, setAddNewDrawerOpen] = useState(false);
    const [editData, setEditData] = useState(null);
    const [sortConfig, setSortConfig] = useState(null);
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const lastFetchParamsRef = React.useRef('');
    const refreshData = useCallback((force = false) => {
        const params = {
            page: currentPage,
            limit: itemsPerPage,
            search: searchTerm || undefined,
            sortBy: sortConfig?.columnId,
            sortOrder: sortConfig?.direction,
            ...extraParams,
        };
        Object.entries(filters).forEach(([key, value]) => {
            if (value !== 'all')
                params[key] = value;
        });
        const paramsString = JSON.stringify(params);
        if (!force && lastFetchParamsRef.current === paramsString)
            return;
        lastFetchParamsRef.current = paramsString;
        dispatch(fetchThunk(params));
    }, [filters, currentPage, searchTerm, sortConfig, dispatch, fetchThunk, itemsPerPage, extraParams]);
    useEffect(() => {
        dispatch(setActiveModuleKey(moduleKey));
        dispatch(fetchDropdownOptions());
        dispatch(fetchPreferencesThunk());
        return () => { dispatch(setActiveModuleKey(null)); };
    }, [dispatch, moduleKey]);
    useEffect(() => {
        refreshData();
    }, [refreshData]);
    const handleSortChange = (columnId, direction) => {
        setSortConfig(direction ? { columnId, direction } : null);
        setCurrentPage(1);
    };
    const handleEdit = (data) => {
        setEditData(data);
        setAddNewDrawerOpen(true);
    };
    const handleDeleteClick = (id) => {
        setItemToDelete(id);
        setDeleteModalOpen(true);
    };
    const handleConfirmDelete = async () => {
        if (!itemToDelete)
            return;
        try {
            setDeleting(true);
            await dispatch(deleteThunk(itemToDelete)).unwrap();
            toast.success("Record deleted successfully.");
            setDeleteModalOpen(false);
            setItemToDelete(null);
            refreshData(true);
        }
        catch (error) {
        }
        finally {
            setDeleting(false);
        }
    };
    const handleColumnVisibilityChange = useCallback((newHidden) => {
        const currentPrefs = preferences?.[moduleKey] || [];
        const orderMap = new Map();
        currentPrefs.forEach((item) => {
            const key = Object.keys(item)[0];
            orderMap.set(key, item[key]?.order);
        });
        const newPrefs = columns.map((col, idx) => {
            if (col.id === 'selection')
                return null;
            return {
                [col.id]: {
                    visibility: !newHidden.has(col.id),
                    order: orderMap.get(col.id) ?? (idx + 1)
                }
            };
        }).filter(Boolean);
        dispatch(savePreferencesThunk({
            preferences: { ...preferences, [moduleKey]: newPrefs }
        }));
    }, [dispatch, preferences, moduleKey, columns]);
    const handleColumnOrderChange = useCallback((newOrder) => {
        const currentPrefs = preferences?.[moduleKey] || [];
        const visibilityMap = new Map();
        currentPrefs.forEach((item) => {
            const key = Object.keys(item)[0];
            visibilityMap.set(key, item[key]?.visibility);
        });
        const newPrefs = newOrder.map((id, idx) => {
            if (id === 'selection')
                return null;
            return {
                [id]: {
                    visibility: visibilityMap.get(id) ?? true,
                    order: idx + 1
                }
            };
        }).filter(Boolean);
        dispatch(savePreferencesThunk({
            preferences: { ...preferences, [moduleKey]: newPrefs }
        }));
    }, [dispatch, preferences, moduleKey]);
    const handleExport = useCallback(async () => {
        if (exporting)
            return;
        try {
            setExporting(true);
            const selectedIds = Array.from(selectedRows);
            // Build filter params (without page/limit — we'll set those per batch)
            const baseParams = {
                search: searchTerm || undefined,
                sortBy: sortConfig?.columnId,
                sortOrder: sortConfig?.direction,
                ...extraParams,
            };
            filtersConfig.forEach(f => {
                if (filters[f.key] !== 'all')
                    baseParams[f.key] = filters[f.key];
            });
            // ✅ Paginated batching: collect all records in EXPORT_BATCH_SIZE chunks
            let allData = [];
            let currentPage = 1;
            let totalPages = 1;
            do {
                const response = await dispatch(fetchThunk({
                    ...baseParams,
                    page: currentPage,
                    limit: EXPORT_BATCH_SIZE,
                })).unwrap();
                const batch = response?.data?.result?.data || [];
                allData = allData.concat(batch);
                const total = response?.data?.result?.total ?? 0;
                totalPages = total > 0 ? Math.ceil(total / EXPORT_BATCH_SIZE) : 1;
                currentPage++;
            } while (currentPage <= totalPages);
            if (allData.length === 0) {
                toast.error('No records found to export.');
                return;
            }
            let dataToExport = allData;
            if (selectedIds.length > 0) {
                dataToExport = allData.filter((r) => selectedIds.includes(r._id));
            }
            exportFn(dataToExport);
        }
        catch (error) {
            toast.error("Export failed. Please try again.");
        }
        finally {
            setExporting(false);
            refreshData(true); // Restore current page
        }
    }, [exporting, selectedRows, filters, searchTerm, sortConfig, dispatch, fetchThunk, filtersConfig, exportFn, refreshData, extraParams]);
    const tableRows = useMemo(() => items?.map((item, i) => ({ id: item._id || `row-${i}`, ...item })) || [], [items]);
    const hiddenColumns = useMemo(() => getHiddenColumnsFromPreferences(preferences, moduleKey), [preferences, moduleKey]);
    const columnOrder = useMemo(() => getColumnOrderFromPreferences(preferences, moduleKey, columns), [preferences, moduleKey, columns]);
    const updateFilter = (key, val) => {
        setFilters(prev => ({ ...prev, [key]: val }));
        setCurrentPage(1);
    };
    return (_jsxs(PageContainer, { children: [_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 1, height: 'calc(100vh - 90px)', overflow: 'hidden' }, children: [_jsx(PagesCommonHeader, { height: "65px", lastUpdatedSection: _jsx(LastUpdatedInfo, { updatedAt: lastUpdated, showIcon: false, moduleName: moduleKey }), rightSection: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: [renderHeaderExtra && renderHeaderExtra(), filtersConfig.map(({ key, icon, label, options }) => (_jsx(Dropdown, { value: filters[key], options: options, onChange: (val) => updateFilter(key, val), displayValue: _jsx(DropdownDisplay, { icon: icon, label: label, value: options.find(o => o.value === filters[key])?.label || 'All' }) }, key)))] }) }), _jsxs(Paper, { elevation: 0, sx: { border: '1px solid #D0D5DD', borderRadius: 2, overflow: 'hidden', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }, children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1, p: 0, borderBottom: '1px solid #EAECF0', flexShrink: 0 }, children: [_jsx(TableToolbar, { title: "", showSearch: true, onSearchChange: (val) => { setSearchTerm(val); setCurrentPage(1); }, showRows: true, showExpand: true, showFullScreen: true, enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchor(e.currentTarget), showFilterRow: true }), _jsx(ExportButton, { onClick: handleExport, loading: exporting }), hasManageAccess && (_jsx(Button, { variant: "outlined", startIcon: _jsx(CloudUpload, { size: 16 }), onClick: () => setUploadDrawerOpen(true), className: 'all-Font-Sizes', sx: { color: '#344054', borderColor: '#D0D5DD', textTransform: 'none', '&:hover': { borderColor: '#98A2B3', bgcolor: '#F9FAFB' } }, children: uploadButtonLabel })), hasManageAccess && (_jsx(Button, { variant: "contained", startIcon: _jsx(Plus, { size: 16 }), onClick: () => setAddNewDrawerOpen(true), className: 'all-Font-Sizes', sx: { bgcolor: '#EE2A7B', textTransform: 'none', '&:hover': { bgcolor: '#D91F6A' } }, children: addNewButtonLabel }))] }), _jsx(Box, { sx: {
                                    overflow: 'hidden',
                                    scrollbarWidth: 'none', // For Firefox
                                    msOverflowStyle: 'none', // For IE/Edge
                                    flex: 1,
                                    minHeight: 0,
                                    '&::-webkit-scrollbar': { display: 'none' }, // For Chrome/Safari
                                    // Give the table's scroll container a definite height so its maxHeight:100%
                                    // resolves and the BODY scrolls internally — keeping the header row sticky.
                                    '& .MuiTableContainer-root': {
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        '&::-webkit-scrollbar': { display: 'none' },
                                    },
                                    '& .reusable-table-container': {
                                        height: '100%',
                                        scrollbarWidth: 'none',
                                        msOverflowStyle: 'none',
                                        '&::-webkit-scrollbar': { display: 'none' },
                                    },
                                    '& .reusable-table-header-cell-pinned-left-last, & .reusable-table-cell-pinned-left-last': {
                                        borderRight: '1px solid #EAECF0 !important',
                                    },
                                    '& .reusable-table-header-cell-pinned-right-first, & .reusable-table-cell-pinned-right-first': {
                                        borderLeft: '1px solid #EAECF0 !important',
                                    },
                                    '& .reusable-table-cell, & .reusable-table-header-cell': {
                                        whiteSpace: 'nowrap',
                                    }
                                }, className: 'user-list-table-wrapper directory-table-wrapper', children: !tableRows.length ? (_jsx(EmptyState, { loading: loading || items === null })) : (_jsx(CommonTable, { columns: columns, rows: tableRows, sortConfig: sortConfig, onSortChange: handleSortChange, expandTrigger: "none", enableSelection: true, selectedRows: selectedRows, onSelectionChange: setSelectedRows, isLoading: loading, renderCell: (row, col) => {
                                        if (col.id === 'action') {
                                            return _jsx(ActionButtons, { row: row, moduleKey: moduleKey, onEdit: handleEdit, onDelete: handleDeleteClick, onSuccess: () => refreshData(true) });
                                        }
                                        return renderCell(row, col, handleEdit, () => refreshData(true));
                                    }, stickyHeader: true, maxHeight: "100%", pinnedColumns: { left: ['selection', 'category'], right: ['action'] }, hiddenColumns: hiddenColumns, onColumnVisibilityChange: handleColumnVisibilityChange, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), columnVisibilityConfig: {
                                        enable: true,
                                        columns: columns.filter(c => c.id !== 'selection' && c.id !== 'action').map(c => c.id),
                                        enableReorder: true
                                    }, enableColumnReorder: true, columnOrder: columnOrder, onColumnOrderChange: handleColumnOrderChange })) }), total > 0 && (_jsx(Box, { sx: { px: 2, py: 0, flexShrink: 0 }, children: _jsx(Pagination, { currentPage: currentPage, totalPages: Math.ceil(total / itemsPerPage), onPageChange: setCurrentPage, maxVisiblePages: 5, totalEntries: total, itemsPerPage: itemsPerPage, showEntriesMessage: true }) }))] })] }), _jsx(UploadDrawer, { open: uploadDrawerOpen, onClose: () => setUploadDrawerOpen(false), onSuccess: () => refreshData(true), moduleKey: moduleKey }), _jsx(AddNewDrawer, { open: addNewDrawerOpen, onClose: () => {
                    setAddNewDrawerOpen(false);
                    setEditData(null);
                }, editData: editData, moduleKey: moduleKey, onSuccess: () => refreshData(true) }), _jsx(ConfirmActionModal, { open: deleteModalOpen, title: `Delete ${pageTitle}`, description: `Are you sure you want to delete this ${pageTitle.toLowerCase()}? This action cannot be undone.`, confirmText: "Delete", onConfirm: handleConfirmDelete, onClose: () => {
                    setDeleteModalOpen(false);
                    setItemToDelete(null);
                }, loading: deleting })] }));
};
export default DirectoryBase;
